import React from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import * as Yup from 'yup';
import { Formik, getIn } from 'formik';
import { Button, Grid } from '@material-ui/core';
import { FarmRiskType, lookupsJson } from '@ourbranch/lookups';

import { isAddressRequired } from 'common/helpers/yup-helpers';
import { useDisabledState } from 'common/disabled-context';
import Field from 'core/components/form/form.v2';
import Label from 'core/components/label/label';
import { LabelTooltip } from 'core/components/label-tooltip';
import InfoIcon from 'core/assets/svg/ic-info-dark.svg?react';
import { NotificationCard } from 'core/components/notification-card';
import useStyles from './risk.styles';

const riskTypeOptions = lookupsJson.farm.riskType.filter(
  (option) => ![FarmRiskType.Fo6Scheduled, FarmRiskType.Fo6Unscheduled].includes(option.id)
);

// Options for FO3 and FO4 fields
const propertyTypeOptions = lookupsJson.farm.propertyType;
const residenceUsageOptions = lookupsJson.farm.residenceUsage;
const constructionTypeOptions = lookupsJson.farm.dwellingConstructionType;

const risksThatRequireFullAddress = [
  FarmRiskType.Fo1,
  FarmRiskType.Fo2,
  FarmRiskType.Fo3,
  FarmRiskType.Fo4,
  FarmRiskType.Fo6Structures
];
const isFullAddressRequired = function (message, path = 'useApplicantAddress') {
  return this.test('required-if-risk-needs-full-address', message, function (value) {
    const useApplicantAddress = getIn(this.options.context, path);
    if (useApplicantAddress) {
      return true;
    }
    const riskType = getIn(this.options.context, 'type');
    if (risksThatRequireFullAddress.includes(riskType)) {
      return !!value;
    }
    return true;
  });
};

Yup.addMethod(Yup.string, 'isAddressRequired', isAddressRequired);
Yup.addMethod(Yup.string, 'isFullAddressRequired', isFullAddressRequired);
const validationSchema = () =>
  Yup.object().shape({
    description: Yup.string().required('Risk Name is required'),
    type: Yup.string().required('Dwelling or structure type is required'),
    building: Yup.object().when('type', {
      is: (value) => value === FarmRiskType.Fo3 || value === FarmRiskType.Fo4,
      then: (schema) =>
        schema.shape({
          propertyType: Yup.string().required('Structure Category is required'),
          residenceUsage: Yup.string().required('Residence Type/Usage is required')
        }),
      otherwise: (schema) =>
        schema.shape({
          propertyType: Yup.string(),
          residenceUsage: Yup.string()
        })
    }),
    address: Yup.object().shape({
      address: Yup.string().isFullAddressRequired('Address is required'),
      address2: Yup.string().nullable(),
      city: Yup.string().isFullAddressRequired('City is required'),
      state: Yup.string().isAddressRequired('State is required'),
      zip: Yup.string().isAddressRequired('Zip code is required')
    })
  });

const AddRisk = observer(({ onAdd, locationAddress, disabled, fieldName }) => {
  const classes = useStyles();
  const { activeForm } = useDisabledState();

  const disableForm = Boolean(activeForm || disabled);

  // Structure categories depend on state, so we need to get them dynamically
  const structureCategories = locationAddress?.state
    ? lookupsJson.farm.structureCategoriesByState[locationAddress.state]
    : [];

  return (
    <Formik
      initialValues={{
        description: '',
        type: undefined,
        propertyType: undefined,
        building: {
          buildingType: undefined,
          propertyType: undefined,
          residenceUsage: undefined,
          constructionType: undefined,
          numSolidFuelBurningAppliances: undefined
        },
        useApplicantAddress: !!locationAddress?.address,
        address: {
          address: undefined,
          address2: undefined,
          city: undefined,
          state: undefined,
          zip: undefined
        }
      }}
      validateOnChange={false}
      validateOnBlur={false}
      validationSchema={validationSchema}
      onSubmit={(values, actions) => {
        const { useApplicantAddress, ...rest } = values;

        if (useApplicantAddress) {
          onAdd({
            ...rest,
            address: locationAddress
          });
        } else {
          onAdd({
            ...rest
          });
        }

        actions.resetForm();
      }}
    >
      {({ values, submitForm, setFieldValue }) => {
        const requiresFullAddress = risksThatRequireFullAddress.includes(values.type);
        return (
          <div className={classes.container}>
            <Label type="infoSubtitleSmall">Add a dwelling or structure</Label>
            <Grid key="container" container justifyContent="space-between" alignItems="center" spacing={2}>
              <Field
                xs={6}
                type="select"
                id={`${fieldName}-type`}
                name="type"
                mode="light"
                label="Risk Type*"
                options={riskTypeOptions}
                disabled={disableForm}
              />
              <Field
                xs={6}
                type="string"
                id={`${fieldName}-description`}
                name="description"
                mode="light"
                label="Risk Name*"
                disabled={disableForm}
              />

              {values.type === FarmRiskType.Fo6Structures && locationAddress?.state && (
                <Field
                  xs={12}
                  type="select"
                  id={`${fieldName}-building.buildingType`}
                  name="building.buildingType"
                  mode="light"
                  label="Structure Category"
                  options={structureCategories}
                  disabled={disableForm}
                />
              )}

              {values.type === FarmRiskType.Fo3 && (
                <>
                  <Field
                    xs={6}
                    type="select"
                    id={`${fieldName}.building.propertyType`}
                    name="building.propertyType"
                    mode="light"
                    label="Structure Category*"
                    options={propertyTypeOptions}
                    disabled={disableForm}
                  />
                  <Field
                    xs={6}
                    type="select"
                    id={`${fieldName}.building.residenceUsage`}
                    name="building.residenceUsage"
                    mode="light"
                    label="Residence Type/Usage*"
                    options={residenceUsageOptions.filter((option) => option.id !== 'C')}
                    disabled={disableForm}
                  />
                </>
              )}

              {values.type === FarmRiskType.Fo4 && (
                <>
                  <Field
                    xs={6}
                    type="select"
                    id={`${fieldName}.building.propertyType`}
                    name="building.propertyType"
                    mode="light"
                    label="Structure Category*"
                    options={propertyTypeOptions.map((option) => ({
                      ...option,
                      value: option.value === 'Home' ? 'Home or Rental Unit' : option.value
                    }))}
                    disabled={disableForm}
                  />
                  <Field
                    xs={6}
                    type="select"
                    id={`${fieldName}.building.residenceUsage`}
                    name="building.residenceUsage"
                    mode="light"
                    label="Residence Type/Usage*"
                    options={residenceUsageOptions}
                    disabled={disableForm}
                  />
                </>
              )}

              {/* address section */}
              {locationAddress?.address && (
                <Field
                  xs={12}
                  type="checkbox"
                  id={`${fieldName}-useApplicantAddress`}
                  name="useApplicantAddress"
                  mode="light"
                  fast={false}
                  label={
                    <span>
                      Use{' '}
                      <b>
                        {[locationAddress.address, locationAddress.city, locationAddress.state, locationAddress.zip]
                          .filter(Boolean)
                          .join(', ')}
                      </b>{' '}
                      as the address for this risk
                    </span>
                  }
                  disabled={disableForm}
                />
              )}
              {!values.useApplicantAddress && requiresFullAddress && (
                <>
                  <Field
                    xs={8}
                    type="address"
                    id={`${fieldName}-address.address`}
                    name="address.address"
                    mode="light"
                    label="Address*"
                    disabled={disableForm}
                  />
                  <Field
                    xs={4}
                    type="string"
                    id={`${fieldName}-address.address2`}
                    name="address.address2"
                    mode="light"
                    label="Apt, unit, building, etc."
                    disabled={disableForm}
                  />
                  <Field
                    xs={4}
                    type="string"
                    id={`${fieldName}-address.city`}
                    name="address.city"
                    mode="light"
                    label="City*"
                    disabled={disableForm}
                  />
                  <Field
                    xs={4}
                    type="state"
                    id={`${fieldName}-address.state`}
                    name="address.state"
                    mode="light"
                    label="State*"
                    disabled={disableForm}
                  />
                  <Field
                    xs={4}
                    type="string"
                    id={`${fieldName}-address.zip`}
                    name="address.zip"
                    mode="light"
                    label="Zip code*"
                    disabled={disableForm}
                  />
                </>
              )}
              {!values.useApplicantAddress && !requiresFullAddress && (
                <>
                  <Field
                    xs={6}
                    type="string"
                    id={`${fieldName}-address.state`}
                    name="address.state"
                    mode="light"
                    label="State*"
                    disabled={disableForm}
                  />
                  <Field
                    xs={6}
                    type="string"
                    id={`${fieldName}-address.zip`}
                    name="address.zip"
                    mode="light"
                    label="Zip Code*"
                    disabled={disableForm}
                  />
                </>
              )}
              {values.type === FarmRiskType.Fo4 && (
                <Grid item xs={12} className={classes.rentersInfoTextContainer}>
                  <InfoIcon width={16} height={16} />
                  <span className={classes.rentersInfoText}>
                    Make sure to include a floor, unit, or building number if one exists for this rental.
                  </span>
                </Grid>
              )}
              <Grid item container justifyContent="flex-end" xs={12}>
                <Button
                  type="button"
                  key="addButton"
                  mode="big"
                  variant="contained"
                  color="secondary"
                  className={classes.smallButton}
                  disabled={disableForm}
                  onClick={submitForm}
                >
                  Add
                </Button>
              </Grid>
            </Grid>
            {disableForm && (
              <NotificationCard className={classes.notification} iconSize="icon16" type="infoGreen">
                <Label>You can't add a dwelling or structure, or any risk, until you update the offer.</Label>
              </NotificationCard>
            )}
          </div>
        );
      }}
    </Formik>
  );
});

AddRisk.propTypes = {
  onAdd: PropTypes.func.isRequired,
  locationAddress: PropTypes.shape({
    address: PropTypes.string,
    address2: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    zip: PropTypes.string
  }),
  disabled: PropTypes.bool,
  fieldName: PropTypes.string.isRequired
};

AddRisk.defaultProps = {
  disabled: false
};

export default AddRisk;
